/**
 * FS2 packaging catalog for Lane planner (Zoomlogi-only; not sent to cargo.one).
 * Labels are "Supplier / solution - Type".
 * `planningBudgetHours` is the upper bound used to flag over-budget air quotes.
 */
export interface PackagingOption {
  value: string;
  label: string;
  temperatureRange: string;
  publishedStability: string;
  planningBudget: string;
  /** Upper bound of Planning budget in hours (for transitTime comparison). */
  planningBudgetHours: number;
}

export const PACKAGING_OPTIONS: PackagingOption[] = [
  {
    value: 'QProducts PalletQuilt - Thermal blanket',
    label: 'QProducts PalletQuilt - Thermal blanket',
    temperatureRange: '15–25°C; limited 2–8°C',
    publishedStability: 'No universal validated duration',
    planningBudget: '8–12 hrs',
    planningBudgetHours: 12,
  },
  {
    value: 'Basic reflective pallet cover - Thermal blanket',
    label: 'Basic reflective pallet cover - Thermal blanket',
    temperatureRange: 'Mainly CRT/excursion protection',
    publishedStability: 'Short-term/tarmac protection',
    planningBudget: '2–6 hrs',
    planningBudgetHours: 6,
  },
  {
    value: 'Qualified insulated blanket with base pad - Thermal blanket',
    label: 'Qualified insulated blanket with base pad - Thermal blanket',
    temperatureRange: '15–25°C; sometimes 2–8°C',
    publishedStability: 'Lane/configuration-specific',
    planningBudget: '12–24 hrs',
    planningBudgetHours: 24,
  },
  {
    value: 'Cryopak Q-Pak TimeSaver - Passive parcel shipper',
    label: 'Cryopak Q-Pak TimeSaver - Passive parcel shipper',
    temperatureRange: '2–8°C / CRT',
    publishedStability: '48 hrs',
    planningBudget: '48 hrs',
    planningBudgetHours: 48,
  },
  {
    value: 'Cryopak CryoCube - Passive parcel shipper',
    label: 'Cryopak CryoCube - Passive parcel shipper',
    temperatureRange: '2–8°C / CRT',
    publishedStability: '62–96 hrs',
    planningBudget: '72 hrs',
    planningBudgetHours: 72,
  },
  {
    value: 'CSafe APS - Passive pallet shipper',
    label: 'CSafe APS - Passive pallet shipper',
    temperatureRange: '2–8°C, 15–25°C, below –20°C',
    publishedStability: '120+ hrs',
    planningBudget: '120 hrs',
    planningBudgetHours: 120,
  },
  {
    value: 'va-Q-tainer / Envirotainer ProofTainer - Passive pallet shipper',
    label: 'va-Q-tainer / Envirotainer ProofTainer - Passive pallet shipper',
    temperatureRange: '–70°C to +25°C, configuration-dependent',
    publishedStability: '120+ hrs',
    planningBudget: '120 hrs',
    planningBudgetHours: 120,
  },
  {
    value: 'CSafe RAP - Active container',
    label: 'CSafe RAP - Active container',
    temperatureRange: '2–8°C / 15–25°C',
    publishedStability: '120+ hrs',
    planningBudget: '120 hrs unplugged',
    planningBudgetHours: 120,
  },
  {
    value: 'CSafe RKN - Active container',
    label: 'CSafe RKN - Active container',
    temperatureRange: '2–8°C / 15–25°C',
    publishedStability: '130+ hrs',
    planningBudget: '130 hrs unplugged',
    planningBudgetHours: 130,
  },
  {
    value: 'Envirotainer Releye - Active container',
    label: 'Envirotainer Releye - Active container',
    temperatureRange: 'Primarily 2–8°C; model-dependent',
    publishedStability: 'Up to 170 hrs',
    planningBudget: '150–170 hrs',
    planningBudgetHours: 170,
  },
  {
    value: 'SkyCell 1500X - Hybrid/passive container',
    label: 'SkyCell 1500X - Hybrid/passive container',
    temperatureRange: '2–8°C / 15–25°C',
    publishedStability: 'Up to 270 hrs',
    planningBudget: '200–270 hrs',
    planningBudgetHours: 270,
  },
  {
    value: 'SkyCell 6500X CRT - Hybrid/passive pallet container',
    label: 'SkyCell 6500X CRT - Hybrid/passive pallet container',
    temperatureRange: '15–25°C',
    publishedStability: '300 hrs at 20°C ambient',
    planningBudget: '240–300 hrs',
    planningBudgetHours: 300,
  },
];

export const findPackagingOption = (
  value?: string | null
): PackagingOption | undefined =>
  value ? PACKAGING_OPTIONS.find((o) => o.value === value) : undefined;

/** True when air transitTime (minutes) exceeds packaging Planning budget. */
export const isTransitOverPackagingBudget = (
  transitTimeMinutes: number | null | undefined,
  packaging?: PackagingOption | null
): boolean => {
  if (!packaging || transitTimeMinutes == null || transitTimeMinutes <= 0) {
    return false;
  }
  return transitTimeMinutes > packaging.planningBudgetHours * 60;
};
